import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { Building2, Users, Snowflake, Car, SlidersHorizontal, X } from "lucide-react";

const AC_OPTIONS = ["All", "AC", "Non-AC", "AC & Non-AC"];
const CAPACITY_OPTIONS = [
  { label: "Any capacity", value: 0 },
  { label: "100+ seats", value: 100 },
  { label: "300+ seats", value: 300 },
  { label: "500+ seats", value: 500 },
  { label: "1000+ seats", value: 1000 },
];
const BUDGET_OPTIONS = [
  { label: "Any budget", value: 0 },
  { label: "Under ₹25,000", value: 25000 },
  { label: "Under ₹50,000", value: 50000 },
  { label: "Under ₹1,00,000", value: 100000 },
  { label: "Under ₹2,00,000", value: 200000 },
];

export default function Halls() {
  const { data: halls = [], isLoading } = useQuery({ queryKey: ["halls"], queryFn: async () => (await api.get("/halls")).data });

  const [ac, setAc] = useState("All");
  const [minCapacity, setMinCapacity] = useState(0);
  const [maxBudget, setMaxBudget] = useState(0);

  const cities = useMemo(() => ["All", ...Array.from(new Set(halls.map((h) => h.city).filter(Boolean)))], [halls]);
  const [city, setCity] = useState("All");

  const filtered = useMemo(() => halls.filter((h) => {
    if (ac !== "All" && h.ac !== ac) return false;
    if (city !== "All" && h.city !== city) return false;
    if (minCapacity && (h.seat_capacity || 0) < minCapacity) return false;
    if (maxBudget && (h.price || 0) > maxBudget) return false;
    return true;
  }), [halls, ac, city, minCapacity, maxBudget]);

  const activeFilters = (ac !== "All") + (city !== "All") + (minCapacity > 0) + (maxBudget > 0);
  const clearFilters = () => { setAc("All"); setCity("All"); setMinCapacity(0); setMaxBudget(0); };

  const selCls = "h-10 rounded-full border border-stone-200 bg-card px-4 text-sm font-medium focus:outline-none focus:border-primary";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 pb-24" data-testid="halls-page">
      <div className="flex items-center gap-2 text-primary font-semibold text-sm"><Building2 className="h-4 w-4" /> Halls & Convention Centres</div>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mt-1">Book a hall or choultry</h1>
      <p className="text-muted-foreground mt-2">Kalyana mantapas, convention centres & choultries with capacity and pricing upfront.</p>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="halls-filters">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground"><SlidersHorizontal className="h-4 w-4" /> Filters</span>
        {cities.length > 2 && (
          <select data-testid="halls-filter-city" className={selCls} value={city} onChange={(e) => setCity(e.target.value)}>
            {cities.map((c) => <option key={c} value={c}>{c === "All" ? "All cities" : c}</option>)}
          </select>
        )}
        <select data-testid="halls-filter-ac" className={selCls} value={ac} onChange={(e) => setAc(e.target.value)}>
          {AC_OPTIONS.map((o) => <option key={o} value={o}>{o === "All" ? "AC / Non-AC" : o}</option>)}
        </select>
        <select data-testid="halls-filter-capacity" className={selCls} value={minCapacity} onChange={(e) => setMinCapacity(Number(e.target.value))}>
          {CAPACITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select data-testid="halls-filter-budget" className={selCls} value={maxBudget} onChange={(e) => setMaxBudget(Number(e.target.value))}>
          {BUDGET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {activeFilters > 0 && (
          <button data-testid="halls-filter-clear" onClick={clearFilters} className="flex items-center gap-1 h-10 px-3 text-sm font-semibold text-primary hover:bg-primary/5 rounded-full">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      {isLoading && <p className="text-muted-foreground mt-8">Loading halls…</p>}
      {!isLoading && halls.length === 0 && <p className="text-muted-foreground mt-8" data-testid="halls-empty">No halls listed yet. Please check back soon.</p>}
      {!isLoading && halls.length > 0 && (
        <p className="text-sm text-muted-foreground mt-4" data-testid="halls-count">{filtered.length} {filtered.length === 1 ? "hall" : "halls"} found</p>
      )}
      {!isLoading && halls.length > 0 && filtered.length === 0 && (
        <p className="text-muted-foreground mt-8" data-testid="halls-no-match">No halls match your filters. <button onClick={clearFilters} className="text-primary font-semibold underline">Clear filters</button></p>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {filtered.map((h) => (
          <Link key={h.id} to={`/halls/${h.id}`} data-testid={`hall-card-${h.id}`} className="bg-card border border-stone-200 rounded-2xl overflow-hidden flex flex-col hover:border-primary transition-colors">
            {h.cover ? <img src={imgUrl(h.cover)} alt={h.name} className="h-44 w-full object-cover" /> : <div className="h-44 w-full bg-gradient-to-br from-primary/20 to-secondary/20" />}
            <div className="p-4 flex-1 flex flex-col">
              <span className="text-xs text-primary font-semibold">{h.hall_type}</span>
              <h3 className="font-display font-bold text-lg leading-tight">{h.name}</h3>
              <p className="text-xs text-muted-foreground">{h.city}</p>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-2">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {h.seat_capacity || "—"} seats</span>
                <span className="flex items-center gap-1"><Snowflake className="h-3.5 w-3.5" /> {h.ac}</span>
                {h.parking_capacity ? <span className="flex items-center gap-1"><Car className="h-3.5 w-3.5" /> {h.parking_capacity}</span> : null}
              </div>
              <div className="mt-auto pt-3">
                <span className="text-xs text-muted-foreground">from </span>
                <span className="font-display font-extrabold text-lg">{inr(h.price)}</span>
                <span className="text-xs text-muted-foreground"> / event</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
